import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  getInspectionUrl,
  INSPECTION_URL_TYPE_API,
  setInspectionOffline,
} from "../../lib/calculation";
import { get, removeRequestFromDbIndex } from "../../lib/comm";
import {
  MainContext,
  STATUS_ONLINE,
  UIContext,
  UpdateContext,
} from "../../main-context";

export default function LoadBar() {
  const [loading_type, set_loading_type] = useState("monthly");
  const main_context = useContext(MainContext);
  const history = useHistory();
  const location = useLocation();
  const update_context = useContext(UpdateContext);
  const [progress, set_progress] = useState(false);
  const [error, set_error] = useState(false);
  const [progress_loaded, set_progress_loaded] = useState(0);
  const [show, set_show] = useState(true);
  const [done, set_done] = useState(false);
  // const [retryMode, setRetryMode] = useState(false);

  const inspectionTypes = useMemo(() => {
    if (location.pathname.indexOf("daily") !== -1) {
      return ["daily"];
    } else if (location.pathname.indexOf("weekly") !== -1) {
      return ["weekly"];
    } else if (location.pathname.indexOf("fso") !== -1) {
      return ["fso"];
    } else {
      return ["monthly"];
    }
  }, [location.pathname]);

  const valid = useMemo(
    () =>
      !!(
        inspectionTypes &&
        main_context.inspections &&
        main_context.inspections[loading_type]
      ),
    [inspectionTypes, loading_type, main_context.inspections]
  );

  async function inspectionLoader(
    inspections,
    history,
    loading_type,
    main_context_status_id,
    update_context
  ) {
    return new Promise((resolve, reject) => {
      const p = [];
      const g = [];

      for (const inspection of inspections) {
        const url = getInspectionUrl(
          INSPECTION_URL_TYPE_API,
          inspection,
          null,
          loading_type === "fso"
        );
        removeRequestFromDbIndex(url);
        const main = get(
          url,
          {},
          main_context_status_id,
          update_context,
          history,
          true
        ).then((result) => {
          if (result !== "comm error") {
            if (result.errorHtml && result.footer) {
              const sub = get(
                url + "?partial=true",
                {},
                main_context_status_id,
                update_context,
                history
              ).then((partial_result) => {
                if (partial_result !== "comm error") {
                  set_progress_loaded((prev) => prev + 1);
                  setInspectionOffline(
                    update_context.update_main_context,
                    loading_type,
                    inspection.id
                  );
                } else {
                  set_error(true);
                }
              });
              g.push(sub);
            } else {
              set_progress_loaded((prev) => prev + 1);
              setInspectionOffline(
                update_context.update_main_context,
                loading_type,
                inspection.id
              );
            }
          } else {
            set_error(true);
          }
        });
        p.push(main);
      }

      Promise.all(p).then(() => {
        Promise.all(g).then(() => {
          resolve();
        });
      });
    });
  }

  const loadInspection = useCallback(async () => {
    if (valid && !progress && !error) {
      set_progress(true);
      set_progress_loaded(0);

      const parents = main_context.inspections[loading_type].filter(
        (x) => x.parentId === null
      );

      await inspectionLoader(
        parents,
        history,
        loading_type,
        main_context.status.id,
        update_context
      );

      const childs = main_context.inspections[loading_type].filter(
        (x) => x.parentId !== null
      );

      await inspectionLoader(
        childs,
        history,
        loading_type,
        main_context.status.id,
        update_context
      );

      set_progress(false);
    }
  }, [
    error,
    history,
    loading_type,
    main_context.inspections,
    main_context.status.id,
    progress,
    update_context,
    valid,
  ]);

  useEffect(() => {
    set_loading_type(inspectionTypes[0]);
    set_error(false);
    set_show(true);
    set_done(false);
    set_progress(false);
  }, [inspectionTypes, main_context.status.preferences.operationId]);

  useEffect(() => {
    let handler;
    if (valid && !progress) {
      let inner_loaded = 0;
      for (const inspection of main_context.inspections[loading_type]) {
        if (inspection.offline_exists) {
          inner_loaded++;
        }
      }

      const inspectionTotal = main_context.inspections[loading_type].length;
      const all_loaded = inspectionTotal === inner_loaded;
      set_done(all_loaded);
      if (all_loaded) {
        handler = window.setTimeout(() => {
          set_show(false);
        }, 3000);
      }
    }

    return () => {
      if (handler) {
        window.clearTimeout(handler);
      }
    };
  }, [
    inspectionTypes,
    loading_type,
    main_context.inspections,
    progress,
    valid,
  ]);

  return (
    <UIContext.Consumer>
      {({ online_status, error }) => (
        <MainContext.Consumer>
          {({ inspections }) => (
            <div
              style={{
                textAlign: "center",
                fontSize: "20px",
                color: "white",
                transition: "max-height 0.5s ease-in-out",
                overflow: "hidden",
                fontWeight: "bold",
                maxHeight:
                  online_status === STATUS_ONLINE && show ? "28px" : "0",
                backgroundColor: done ? "green" : "orange",
              }}
            >
              {error && (
                <span
                  style={{
                    color: "red",
                    marginRight: "20px",
                  }}
                >
                  {error.startsWith("timeout")
                    ? "Couldn't grab whole list! You can retry to finish."
                    : "Inspection grab failed"}
                </span>
              )}
              {!error && valid && (
                <>
                  {(progress && (
                    <span>
                      Grabbing {progress_loaded} of{" "}
                      {inspections[loading_type].length}
                    </span>
                  )) || (
                    <>
                      {(done && <span>Device ready for offline!</span>) || (
                        <span
                          style={{
                            cursor: "pointer",
                          }}
                          onClick={() => loadInspection()}
                        >
                          Enable Offline Mode
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </MainContext.Consumer>
      )}
    </UIContext.Consumer>
  );
}
